import { ClassValue } from 'clsx';
import DotsIcon from '@shared/assets/icons/v2/dots.svg';
import { ActionSheet } from '@shared/components/ActionSheet';
import { View } from '@shared/components/View';
import { MBIconButton } from '@shared/components/button';
import { Entity } from '@shared/data/entity/Entity';
import { CardExport } from '@shared/scenes/cards/details/containers/CardExport';
import { ShareSheet } from '@shared/scenes/cards/details/containers/ShareSheet';
import { useOverflowMenu } from '@shared/scenes/cards/listView/hooks/useOverflowMenu';
import { Colors, getAnchorName } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import styles from './CardOverflowButton.module.css';

interface Props {
  entity: Entity;
  workflowTemplate: API.WorkflowTemplateFragment;
  backgroundColor?: string;
  onPressOpenEntity: VoidFunction;
  webHorizontalAlignment?: 'left' | 'right';
  className?: ClassValue;
}

export function CardOverflowButton({
  entity,
  workflowTemplate,
  backgroundColor = Colors.v2.gray05,
  onPressOpenEntity,
  webHorizontalAlignment = 'right',
  className,
}: Props) {
  const anchorName = getAnchorName('card-overflow', entity.id);

  const {
    shouldShowOverflowMenu,
    isMenuOpen,
    openMenu,
    closeMenu,
    menuOptions,
    isDeleteConfirmOpen,
    closeDeleteConfirm,
    confirmDeleteOptions,
    isShareSheetOpen,
    closeShareSheet,
    isDownloadSheetOpen,
    closeDownloadSheet,
  } = useOverflowMenu({ entity, onPressOpenEntity, workflowTemplate });

  if (!shouldShowOverflowMenu) {
    return null;
  }

  return (
    <View className={className}>
      <View style={{ anchorName }}>
        <MBIconButton
          testID="card-overflow-button"
          source={DotsIcon}
          size="small"
          inverted
          onPress={openMenu}
          backgroundColor={backgroundColor}
        />
      </View>
      {isMenuOpen && (
        <ActionSheet
          anchorName={anchorName}
          align={webHorizontalAlignment}
          onClose={closeMenu}
          className={styles.cardOverflowMenu}
          testID="card-overflow-menu"
          options={menuOptions}
        />
      )}
      {isDeleteConfirmOpen && (
        <ActionSheet
          anchorName={anchorName}
          title={i18n('card.details.deleteConfirmation.title')}
          onClose={closeDeleteConfirm}
          className={styles.deleteConfirmSheet}
          testID="delete-card-sheet"
          options={confirmDeleteOptions}
        />
      )}
      {isShareSheetOpen && (
        <ShareSheet
          anchorName={anchorName}
          card={{ id: entity.id, workflowTemplateId: workflowTemplate.id }}
          onCloseSheet={closeShareSheet}
        />
      )}
      {isDownloadSheetOpen && (
        <CardExport
          anchorName={anchorName}
          card={{ id: entity.id, workflowTemplateId: workflowTemplate.id }}
          onCloseSheet={closeDownloadSheet}
        />
      )}
    </View>
  );
}
